import { useContext, useEffect, useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { SocketContext } from '@/contexts/rtm';
import useDesktopNotifications from '@/hooks/useDesktopNotifications';
import { onMessageCreated } from '@/hooks/useMessages';
import useNotificationSounds from '@/hooks/useNotificationSounds';
import { patchDetail, patchInLists } from '@/modules/humanChat/hooks/useConversations';
import { invalidateScheduledMessagesOnMessage } from '@/modules/humanChat/hooks/useScheduledMessages';
import { selectAccountId, selectAccountSlug } from '@/redux/session/selectors';
import { AuthorType, MarkMessages } from '@/shared/types/chat';
import { NewMessage, SocketEvent, UserEvent } from '@/shared/types/rtm';

const useRtmMessages = (deskId: string, session_id: string) => {
  const { t } = useTranslation();
  const { showNotifications } = useDesktopNotifications();
  const accountId = useSelector(selectAccountId);
  const { play } = useNotificationSounds();
  const slug = useSelector(selectAccountSlug);
  const { socket, isConnected } = useContext(SocketContext);
  const queryClient = useQueryClient();

  const handleNewMessage = useCallback<UserEvent<NewMessage>>(
    ({ data }) => {
      if (data.session_id === session_id) {
        onMessageCreated(queryClient, deskId, data);
        invalidateScheduledMessagesOnMessage(queryClient, deskId, data.session_id);
      }
      if (data.author_type === 'visitor' && data.session_id === session_id) {
        showNotifications({
          url: `/${slug}/chats/${deskId}/conversations/${data.session_id}`,
          text: t('conversation.you_have_a_new_message'),
        });
        play();
      }
    },
    [deskId, play, queryClient, session_id, showNotifications, slug, t]
  );

  // The Console shows no read receipts; a read of the visitor messages only clears the unread count.
  const handleMessageRead = useCallback<UserEvent<MarkMessages>>(
    ({ data }) => {
      if (data.author_type !== AuthorType.VISITOR) {
        return;
      }
      const readPatch = { session_id: data.session_id, desk_id: deskId, agent_unread_count: 0 };
      patchDetail(queryClient, readPatch);
      patchInLists(queryClient, deskId, readPatch);
    },
    [deskId, queryClient]
  );

  useEffect(() => {
    socket?.on(SocketEvent.messages_read, handleMessageRead);
    return () => {
      socket?.off(SocketEvent.messages_read, handleMessageRead);
    };
  }, [handleMessageRead, socket]);

  useEffect(() => {
    socket?.on(SocketEvent.messages_created, handleNewMessage);
    return () => {
      socket?.off(SocketEvent.messages_created, handleNewMessage);
    };
  }, [handleNewMessage, socket]);

  useEffect(() => {
    if (!socket || !session_id || !accountId || !isConnected) {
      return;
    }
    socket.emit(SocketEvent.subscribe_conversation, { account_id: accountId, session_id });
    return () => {
      socket.emit(SocketEvent.unsubscribe_conversation, { account_id: accountId, session_id });
    };
  }, [isConnected, session_id, accountId, socket]);
};

export default useRtmMessages;
